import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PropertyHasJobHistoryError, useDeleteProperty } from '@/routes/properties/useProperty';

/**
 * Delete confirmation for a property (M1-05's scope extension) — a hard
 * delete, for a mistaken add with no history. The API blocks it once the
 * property has job history (M1-02); the AC asks that this refusal be
 * SURFACED, not hidden behind an always-disabled button, because the refusal
 * IS the safety property worth showing — archive is presented as the normal
 * action (`propertyDetail.archiveProperty.action`) and this as the exception.
 *
 * `PropertyHasJobHistoryError`'s message is the API's own 409 body verbatim
 * ("This property has job history and cannot be deleted. Archive it
 * instead."), not a locally-authored string — the API is the source of truth
 * for exactly why the refusal happened, and a hand-written client-side
 * paraphrase risks drifting from it.
 */
export function DeletePropertyDialog({
  propertyId,
  propertyLine1,
  onClose,
  onDeleted,
}: {
  propertyId: string;
  propertyLine1: string;
  onClose: () => void;
  /** The property is gone — the caller navigates away from its page. */
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const deleteProperty = useDeleteProperty(propertyId);

  const errorMessage =
    deleteProperty.error instanceof PropertyHasJobHistoryError
      ? deleteProperty.error.message
      : deleteProperty.isError
        ? t('propertyDetail.deleteProperty.failed')
        : undefined;

  return (
    <ConfirmDialog
      titleId="delete-property-title"
      title={t('propertyDetail.deleteProperty.title')}
      description={t('propertyDetail.deleteProperty.description', { line1: propertyLine1 })}
      confirmLabel={
        deleteProperty.isPending
          ? t('propertyDetail.deleteProperty.deleting')
          : t('propertyDetail.deleteProperty.confirm')
      }
      isPending={deleteProperty.isPending}
      errorMessage={errorMessage}
      testId="delete-property-dialog"
      onClose={onClose}
      onConfirm={() =>
        deleteProperty.mutate(undefined, {
          onSuccess: onDeleted,
        })
      }
    />
  );
}
